<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-1">
      <b-button
        size="sm"
        variant="outline-secondary"
        @click="$router.push('/support')"
      >
        <feather-icon icon="ArrowLeftIcon" />
        Back to queue
      </b-button>
      <div v-if="item">
        <b-badge
          :variant="statusVariant(item.status)"
          class="mr-1"
        >
          {{ item.status.replace(/_/g, ' ') }}
        </b-badge>
        <small class="text-muted">#{{ item.id }}</small>
      </div>
    </div>

    <b-alert
      :show="!!error"
      variant="danger"
    >
      <div class="alert-body">
        {{ error }}
      </div>
    </b-alert>

    <b-row v-if="item">
      <!-- the customer's email -->
      <b-col
        cols="12"
        lg="6"
      >
        <b-card no-body>
          <b-card-header>
            <b-card-title class="mb-0">
              Customer message
            </b-card-title>
          </b-card-header>
          <b-card-body>
            <h5>{{ item.subject }}</h5>
            <p class="mb-1">
              <strong>{{ item.from_name || item.from_addr }}</strong>
              <span v-if="item.from_name"> &lt;{{ item.from_addr }}&gt;</span>
              <br>
              <small class="text-muted">{{ item.received_at }}</small>
            </p>
            <p
              v-if="item.category"
              class="mb-1"
            >
              <b-badge variant="light-primary">
                {{ item.category.replace(/_/g, ' ') }}
              </b-badge>
              <small
                v-if="item.summary"
                class="ml-1 text-muted"
              >{{ item.summary }}</small>
            </p>
            <pre class="email-body">{{ item.body }}</pre>
            <b-button
              size="sm"
              variant="outline-primary"
              :to="`/accounts/${encodeURIComponent(item.from_addr)}`"
            >
              <feather-icon icon="UserIcon" />
              Open account
            </b-button>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- the bot's draft -->
      <b-col
        cols="12"
        lg="6"
      >
        <b-card no-body>
          <b-card-header class="d-flex justify-content-between">
            <b-card-title class="mb-0">
              Draft reply
            </b-card-title>
            <small
              v-if="draft && draft.version"
              class="text-muted"
            >version {{ draft.version }} ({{ draft.created_by === 'bot' ? 'bot' : draft.created_by }})</small>
          </b-card-header>
          <b-card-body>
            <b-alert
              :show="!!item.status_reason && item.status !== 'sent' && item.status !== 'dismissed'"
              :variant="item.status === 'needs_human' ? 'warning' : 'info'"
            >
              <div class="alert-body">
                <strong>{{ item.status === 'needs_human' ? 'Needs a human:' : 'Note:' }}</strong>
                {{ item.status_reason }}
              </div>
            </b-alert>
            <p
              v-if="draft && draft.confidence"
              class="mb-1"
            >
              <small class="text-muted">Bot confidence: <strong>{{ draft.confidence }}</strong></small>
            </p>

            <template v-if="draft && draft.text !== null">
              <b-form-textarea
                v-model="draftText"
                rows="14"
                max-rows="30"
                :disabled="closed"
              />
              <div class="mt-1 d-flex flex-wrap">
                <b-button
                  variant="primary"
                  class="mr-1 mb-50"
                  :disabled="!canSend"
                  @click="askSend"
                >
                  <feather-icon icon="SendIcon" />
                  Send reply
                </b-button>
                <b-button
                  variant="outline-primary"
                  class="mr-1 mb-50"
                  :disabled="closed || !edited"
                  @click="saveDraft"
                >
                  Save edit
                </b-button>
                <b-button
                  variant="outline-secondary"
                  class="mr-1 mb-50"
                  @click="copyDraft"
                >
                  <feather-icon icon="CopyIcon" />
                  Copy
                </b-button>
                <b-button
                  variant="outline-secondary"
                  class="mr-1 mb-50"
                  :disabled="!canRedraft"
                  @click="askRedraft"
                >
                  <b-spinner
                    v-if="redrafting"
                    small
                  />
                  <feather-icon
                    v-else
                    icon="RefreshCwIcon"
                  />
                  {{ redrafting ? 'Re-drafting...' : 'Re-draft' }}
                </b-button>
                <b-button
                  variant="outline-danger"
                  class="mb-50"
                  :disabled="closed"
                  @click="askDismiss"
                >
                  Dismiss
                </b-button>
              </div>
              <small
                v-if="!sendingEnabled && !closed"
                class="text-muted"
              >
                Sending from here is switched off (shadow mode). Copy the text to reply from your mail client.
              </small>
            </template>
            <template v-else>
              <p class="text-muted">
                No draft was written for this message.
              </p>
              <b-button
                variant="outline-secondary"
                class="mr-1"
                :disabled="!canRedraft"
                @click="askRedraft"
              >
                <b-spinner
                  v-if="redrafting"
                  small
                />
                <feather-icon
                  v-else
                  icon="RefreshCwIcon"
                />
                {{ redrafting ? 'Re-drafting...' : 'Re-draft' }}
              </b-button>
              <b-button
                variant="outline-danger"
                :disabled="closed"
                @click="askDismiss"
              >
                Dismiss
              </b-button>
            </template>
          </b-card-body>
        </b-card>

        <!-- rating -->
        <b-card no-body>
          <b-card-header>
            <b-card-title class="mb-0">
              Rate the bot's draft
            </b-card-title>
          </b-card-header>
          <b-card-body>
            <p
              v-if="lastRating"
              class="mb-1"
            >
              Your last rating: <b-badge :variant="ratingVariant(lastRating.verdict)">
                {{ lastRating.verdict }}
              </b-badge>
              <small
                v-if="lastRating.note"
                class="ml-1 text-muted"
              >{{ lastRating.note }}</small>
            </p>
            <b-form-input
              v-model="ratingNote"
              placeholder="Optional note (what was wrong?)"
              maxlength="500"
              class="mb-1"
            />
            <b-button-group>
              <b-button
                variant="outline-success"
                @click="rate('good')"
              >
                Good - would send as is
              </b-button>
              <b-button
                variant="outline-warning"
                @click="rate('edit')"
              >
                Useful after edits
              </b-button>
              <b-button
                variant="outline-danger"
                @click="rate('bad')"
              >
                Not useful
              </b-button>
            </b-button-group>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- proposed action -->
      <b-col
        v-if="draft && draft.proposed_action"
        cols="12"
      >
        <b-card no-body>
          <b-card-header>
            <b-card-title class="mb-0">
              Suggested admin action
            </b-card-title>
          </b-card-header>
          <b-card-body>
            <p class="mb-1">
              <b-badge variant="light-warning">
                {{ draft.proposed_action.type.replace(/_/g, ' ') }}
              </b-badge>
              {{ draft.proposed_action.details }}
            </p>
            <small class="text-muted d-block mb-1">
              The assistant never performs actions. Apply it yourself in the account screen, then reply.
            </small>
            <b-button
              size="sm"
              variant="outline-primary"
              :to="`/accounts/${encodeURIComponent(item.from_addr)}`"
            >
              Open account to apply
            </b-button>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- details -->
      <b-col cols="12">
        <b-card>
          <details v-if="draft && draft.facts">
            <summary>Account facts the assistant used</summary>
            <pre class="email-body mt-1">{{ factsText }}</pre>
          </details>
          <details class="mt-1">
            <summary>History ({{ events.length }})</summary>
            <b-table-simple
              small
              class="mt-1"
            >
              <b-tbody>
                <b-tr
                  v-for="e in events"
                  :key="e.id"
                >
                  <b-td>{{ formatTime(e.ts) }}</b-td>
                  <b-td>{{ e.actor }}</b-td>
                  <b-td>{{ e.kind.replace(/_/g, ' ') }}</b-td>
                  <b-td><small class="text-muted">{{ e.detail }}</small></b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </details>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardHeader, BCardTitle, BCardBody, BBadge, BButton, BButtonGroup, BAlert,
  BFormTextarea, BFormInput, BTableSimple, BTbody, BTr, BTd, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import supportApi from '@/libs/support-api'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BBadge,
    BButton,
    BButtonGroup,
    BAlert,
    BFormTextarea,
    BFormInput,
    BTableSimple,
    BTbody,
    BTr,
    BTd,
    BSpinner,
  },
  data() {
    return {
      item: null,
      draft: null,
      events: [],
      sendingEnabled: false,
      draftText: '',
      ratingNote: '',
      error: '',
      redrafting: false,
      pollTimer: null,
    }
  },
  computed: {
    id() {
      return this.$route.params.id
    },
    closed() {
      return !!this.item && ['sent', 'dismissed', 'answered'].includes(this.item.status)
    },
    edited() {
      return !!this.draft && this.draftText !== this.draft.text
    },
    canRedraft() {
      return !!this.item && !this.redrafting && ['drafted', 'needs_human', 'error'].includes(this.item.status)
    },
    canSend() {
      return !this.closed && this.sendingEnabled && this.draftText.trim().length > 0
    },
    factsText() {
      return JSON.stringify(this.draft.facts, null, 2)
    },
    lastRating() {
      const rated = this.events.filter(e => e.kind === 'rated')
      if (!rated.length) {
        return null
      }
      try {
        return JSON.parse(rated[rated.length - 1].detail)
      } catch (e) {
        return null
      }
    },
  },
  created() {
    this.load()
  },
  beforeDestroy() {
    clearInterval(this.pollTimer)
  },
  methods: {
    load() {
      supportApi('get', `/items/${this.id}`)
        .then(response => {
          this.applyResponse(response.data)
          this.error = ''
        })
        .catch(error => {
          this.error = error.message
        })
    },
    applyResponse(data) {
      this.item = data.item
      this.draft = data.draft
      this.events = data.events
      this.sendingEnabled = data.sending_enabled
      this.draftText = this.draft && this.draft.text ? this.draft.text : ''
    },
    askRedraft() {
      const edited = this.draft && this.draft.created_by && this.draft.created_by !== 'bot'
      const message = edited
        ? 'This draft was edited by a person. A re-draft adds a new bot version that replaces it on screen (the edit stays in the history). Continue?'
        : 'Write a new draft with the current playbooks and facts? The old draft stays in the history.'
      this.$bvModal
        .msgBoxConfirm(message, {
          title: 'Re-draft',
          size: 'sm',
          okVariant: 'primary',
          okTitle: 'Re-draft',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(confirmed => {
          if (confirmed) {
            this.redraft()
          }
        })
    },
    redraft() {
      const baseline = this.events.length
      supportApi('post', `/items/${this.id}/redraft`, {})
        .then(() => {
          this.redrafting = true
          this.pollRedraft(baseline)
        })
        .catch(error => this.notify(error.message, 'warning', 'AlertTriangleIcon'))
    },
    pollRedraft(baseline) {
      const startedAt = Date.now()
      this.pollTimer = setInterval(() => {
        supportApi('get', `/items/${this.id}`)
          .then(response => {
            const finished = response.data.events.slice(baseline)
              .find(e => e.kind === 'processed' || e.kind === 'redraft_failed')
            if (finished) {
              this.stopPolling()
              this.applyResponse(response.data)
              if (finished.kind === 'redraft_failed') {
                this.notify('Re-draft failed; the previous draft was kept', 'warning', 'AlertTriangleIcon')
              } else {
                this.notify('New draft ready')
              }
            } else if (Date.now() - startedAt > 180000) {
              this.stopPolling()
              this.notify('Still working after 3 minutes. Refresh the page in a moment.', 'warning', 'AlertTriangleIcon')
            }
          })
          .catch(() => {})
      }, 3000)
    },
    stopPolling() {
      clearInterval(this.pollTimer)
      this.pollTimer = null
      this.redrafting = false
    },
    notify(title, variant = 'success', icon = 'CheckIcon') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    },
    saveDraft() {
      supportApi('post', `/items/${this.id}/draft`, { text: this.draftText })
        .then(() => {
          this.notify('Edit saved')
          this.load()
        })
        .catch(error => this.notify(error.message, 'danger', 'AlertTriangleIcon'))
    },
    askSend() {
      this.$bvModal
        .msgBoxConfirm(`Send this reply to ${this.item.reply_to || this.item.from_addr}?`, {
          title: 'Please confirm',
          size: 'sm',
          okVariant: 'primary',
          okTitle: 'Send',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(confirmed => {
          if (confirmed) {
            this.send()
          }
        })
    },
    send() {
      supportApi('post', `/items/${this.id}/send`, { text: this.draftText })
        .then(() => {
          this.notify('Reply sent')
          this.load()
        })
        .catch(error => this.notify(error.message, 'warning', 'AlertTriangleIcon'))
    },
    askDismiss() {
      this.$bvModal
        .msgBoxConfirm('Dismiss this message? It will be marked as handled without sending a reply.', {
          title: 'Please confirm',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Dismiss',
          cancelTitle: 'Cancel',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(confirmed => {
          if (confirmed) {
            supportApi('post', `/items/${this.id}/dismiss`, {})
              .then(() => {
                this.notify('Dismissed')
                this.load()
              })
              .catch(error => this.notify(error.message, 'danger', 'AlertTriangleIcon'))
          }
        })
    },
    rate(verdict) {
      supportApi('post', `/items/${this.id}/rate`, { verdict, note: this.ratingNote })
        .then(() => {
          this.notify('Rating saved')
          this.ratingNote = ''
          this.load()
        })
        .catch(error => this.notify(error.message, 'danger', 'AlertTriangleIcon'))
    },
    copyDraft() {
      const done = () => this.notify('Draft copied')
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.draftText).then(done)
      } else {
        const area = document.createElement('textarea')
        area.value = this.draftText
        document.body.appendChild(area)
        area.select()
        document.execCommand('copy')
        document.body.removeChild(area)
        done()
      }
    },
    statusVariant(status) {
      return {
        drafted: 'success',
        needs_human: 'warning',
        error: 'danger',
        sent: 'primary',
        dismissed: 'secondary',
        answered: 'info',
        skipped: 'light',
        new: 'info',
      }[status] || 'secondary'
    },
    ratingVariant(verdict) {
      return { good: 'success', edit: 'warning', bad: 'danger' }[verdict] || 'secondary'
    },
    formatTime(seconds) {
      return seconds ? new Date(seconds * 1000).toLocaleString() : ''
    },
  },
}
</script>

<style scoped>
.email-body {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  background: transparent;
  padding: 0;
  margin: 0 0 1rem;
}
</style>
